<script setup>
import { computed } from 'vue'
import AppHeader from './components/AppHeader.vue'
import StatStrip from './components/StatStrip.vue'
import AlertFeed from './components/AlertFeed.vue'
import ChartPanel from './components/ChartPanel.vue'
import MonthCompare from './components/MonthCompare.vue'
import TransactionList from './components/TransactionList.vue'
import PlanPanel from './components/PlanPanel.vue'
import BudgetPanel from './components/BudgetPanel.vue'
import GoalPanel from './components/GoalPanel.vue'
import Icon from './components/ui/Icon.vue'
import UpdateBanner from './components/UpdateBanner.vue'
import SyncGate from './components/SyncGate.vue'
import TransactionSheet from './components/sheets/TransactionSheet.vue'
import PlanSheet from './components/sheets/PlanSheet.vue'
import BudgetSheet from './components/sheets/BudgetSheet.vue'
import SuggestSheet from './components/sheets/SuggestSheet.vue'
import GoalSheet from './components/sheets/GoalSheet.vue'
import SettingsSheet from './components/sheets/SettingsSheet.vue'
import TagSheet from './components/sheets/TagSheet.vue'
import CategorySheet from './components/sheets/CategorySheet.vue'
import BulkSheet from './components/sheets/BulkSheet.vue'
import ImportSheet from './components/sheets/ImportSheet.vue'
import ConsoleSheet from './components/sheets/ConsoleSheet.vue'
import DuplicatesSheet from './components/sheets/DuplicatesSheet.vue'
import HelpSheet from './components/sheets/HelpSheet.vue'
import DeviceSheet from './components/sheets/DeviceSheet.vue'
import AchievementsSheet from './components/sheets/AchievementsSheet.vue'
import { openSheet, state } from './stores/state'
import { t } from './stores/i18n'
import { alerts, planHealth } from './stores'

const SHEETS = {
  transaction: TransactionSheet,
  plan: PlanSheet,
  budget: BudgetSheet,
  suggest: SuggestSheet,
  goal: GoalSheet,
  settings: SettingsSheet,
  tags: TagSheet,
  categories: CategorySheet,
  bulk: BulkSheet,
  import: ImportSheet,
  console: ConsoleSheet,
  duplicates: DuplicatesSheet,
  help: HelpSheet,
  device: DeviceSheet,
  achievements: AchievementsSheet,
}
const sheet = computed(() => (state.ui.sheet ? SHEETS[state.ui.sheet.name] : null))

const TABS = [
  { value: 'transactions', key: 'ledger.title', icon: 'wallet' },
  { value: 'plan', key: 'tabs.plan', icon: 'repeat' },
  { value: 'budgets', key: 'budgets.title', icon: 'gauge' },
  { value: 'goals', key: 'tabs.goals', icon: 'target' },
]

// Below `lg` the four panels share the screen through this tab strip; from
// `lg` up they are all visible at once and the tabs disappear.
const paneClass = (name) => (state.ui.panel === name ? 'block' : 'hidden lg:block')

const attention = computed(() => alerts.value.filter((a) => a.tone === 'danger').length)
</script>

<template>
  <!-- An encrypted org whose key this device does not hold yet: that is the whole page. -->
  <SyncGate v-if="state.sync.gate" />
  <div v-else class="flex min-h-full flex-col">
    <!-- Everything that stays at the top while scrolling, in one sticky bar that
         starts under the status bar and covers it: see body in style.css. -->
    <div class="sticky top-0 z-30 bg-bg/85 pt-[env(safe-area-inset-top)] backdrop-blur-xl" data-top-bar>
      <UpdateBanner />
      <AppHeader />
    </div>

    <main class="mx-auto w-full max-w-[1400px] flex-1 px-3 pt-3 pb-6 sm:px-5">
      <!-- The sections, one under the other while open. A folded one shrinks to
           its title, and folded titles next to each other share a line: each
           open section takes a whole row, the folded ones wrap between them. -->
      <div class="flex flex-wrap items-start gap-x-6 gap-y-3 lg:gap-y-4">
        <StatStrip />
        <AlertFeed />
        <MonthCompare />
        <ChartPanel />
      </div>

      <!-- Sized to their content, not equal quarters: "Transactions" alone
           needs more than a quarter of a 390px strip, and four equal columns
           truncate every label to fit the longest. The icon is the first thing
           to go on a phone — the word is what names the panel. -->
      <nav class="mt-4 flex gap-1 rounded-xl bg-raised p-1 lg:hidden">
        <button
          v-for="tab in TABS"
          :key="tab.value"
          class="flex flex-auto items-center justify-center gap-1.5 rounded-lg px-0.5 py-2
                 text-[12.5px] font-semibold transition"
          :class="state.ui.panel === tab.value ? 'bg-surface text-text shadow-sm' : 'text-muted'"
          @click="state.ui.panel = tab.value"
        >
          <Icon :name="tab.icon" :size="15" class="hidden shrink-0 sm:block" />
          <span class="truncate">{{ t(tab.key) }}</span>
          <span
            v-if="tab.value === 'plan' && planHealth.missing"
            class="tnum rounded-full bg-danger/15 px-1.5 text-[11px] text-danger"
          >
            {{ planHealth.missing }}
          </span>
        </button>
      </nav>

      <div class="mt-3 grid items-start gap-3 lg:mt-4 lg:grid-cols-[minmax(0,1fr)_23rem] lg:gap-4">
        <!-- min-w-0 throughout: without it a grid column is sized to its
             content and long descriptions push the page sideways. -->
        <!-- `h-0 min-h-full` keeps the ledger out of the row's own sizing while
             still filling it: the panels beside it decide how tall the row is,
             and the ledger scrolls inside whatever that comes to, so the two
             columns end on the same line however long the list gets. -->
        <div class="min-w-0 lg:h-0 lg:min-h-full" :class="paneClass('transactions')">
          <TransactionList />
        </div>
        <div class="grid min-w-0 gap-3 lg:gap-4">
          <div class="min-w-0" :class="paneClass('plan')"><PlanPanel /></div>
          <div class="min-w-0" :class="paneClass('budgets')"><BudgetPanel /></div>
          <div class="min-w-0" :class="paneClass('goals')"><GoalPanel /></div>
        </div>
      </div>
    </main>


    <component :is="sheet" v-if="sheet" :payload="state.ui.sheet.payload" />

    <Transition name="toast">
      <div
        v-if="state.ui.toast"
        class="fixed bottom-24 left-1/2 z-50 -translate-x-1/2 rounded-xl border border-line
               bg-surface px-4 py-2.5 text-[13.5px] font-medium shadow-[var(--c-shadow)]"
        :class="{
          'border-accent/40 text-accent': state.ui.toast.tone === 'good',
          'border-danger/40 text-danger': state.ui.toast.tone === 'danger',
        }"
        role="status"
      >
        {{ state.ui.toast.message }}
      </div>
    </Transition>

    <span v-if="attention" class="sr-only" role="status">
      {{ t('app.attention', { count: attention }) }}
    </span>
  </div>
</template>

<style>
.toast-enter-active,
.toast-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}
.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translate(-50%, 8px);
}
</style>
